/** One line of a rendered difference: kept as it was, added, or taken away. */
export interface DiffLine {
  kind: 'context' | 'add' | 'remove';
  text: string;
}

/**
 * A line-by-line difference between two pieces of text, longest-common-subsequence style.
 *
 * Written here rather than pulled in: the section shows two small diffs — a suggestion's before and
 * after, and what a person typed against the document that moved under them — and a diffing library
 * for a few dozen lines is a dependency the estate would carry for ever.
 *
 * The result is consumed with a textual `−` and `+` on every row, never colour alone (canon §16.6).
 */
export function diffLines(before: string, after: string): DiffLine[] {
  const beforeLines = splitLines(before);
  const afterLines = splitLines(after);
  const common = longestCommonSubsequence(beforeLines, afterLines);

  const lines: DiffLine[] = [];
  let beforeIndex = 0;
  let afterIndex = 0;
  for (const shared of common) {
    while (
      beforeIndex < beforeLines.length &&
      beforeLines[beforeIndex] !== shared
    ) {
      lines.push({ kind: 'remove', text: beforeLines[beforeIndex] ?? '' });
      beforeIndex += 1;
    }
    while (
      afterIndex < afterLines.length &&
      afterLines[afterIndex] !== shared
    ) {
      lines.push({ kind: 'add', text: afterLines[afterIndex] ?? '' });
      afterIndex += 1;
    }
    lines.push({ kind: 'context', text: shared });
    beforeIndex += 1;
    afterIndex += 1;
  }
  for (; beforeIndex < beforeLines.length; beforeIndex += 1) {
    lines.push({ kind: 'remove', text: beforeLines[beforeIndex] ?? '' });
  }
  for (; afterIndex < afterLines.length; afterIndex += 1) {
    lines.push({ kind: 'add', text: afterLines[afterIndex] ?? '' });
  }
  return lines;
}

/** Only the lines that actually changed, for a card that has no room to quote the whole document. */
export function changedLines(before: string, after: string): DiffLine[] {
  return diffLines(before, after).filter((line) => line.kind !== 'context');
}

function splitLines(text: string): string[] {
  if (text.length === 0) return [];
  return text.replace(/\r\n/g, '\n').split('\n');
}

/** The classic dynamic-programming LCS. Inputs here are documents of tens of lines, not thousands. */
function longestCommonSubsequence(
  left: readonly string[],
  right: readonly string[],
): string[] {
  const lengths: number[][] = Array.from({ length: left.length + 1 }, () =>
    new Array<number>(right.length + 1).fill(0),
  );
  for (let leftIndex = left.length - 1; leftIndex >= 0; leftIndex -= 1) {
    for (let rightIndex = right.length - 1; rightIndex >= 0; rightIndex -= 1) {
      const row = lengths[leftIndex] ?? [];
      const nextRow = lengths[leftIndex + 1] ?? [];
      row[rightIndex] =
        left[leftIndex] === right[rightIndex]
          ? (nextRow[rightIndex + 1] ?? 0) + 1
          : Math.max(nextRow[rightIndex] ?? 0, row[rightIndex + 1] ?? 0);
    }
  }

  const shared: string[] = [];
  let leftIndex = 0;
  let rightIndex = 0;
  while (leftIndex < left.length && rightIndex < right.length) {
    if (left[leftIndex] === right[rightIndex]) {
      shared.push(left[leftIndex] ?? '');
      leftIndex += 1;
      rightIndex += 1;
      continue;
    }
    const down = lengths[leftIndex + 1]?.[rightIndex] ?? 0;
    const across = lengths[leftIndex]?.[rightIndex + 1] ?? 0;
    if (down >= across) leftIndex += 1;
    else rightIndex += 1;
  }
  return shared;
}
